import { equals, isEmpty } from 'lodash/fp';

import { FeatureFlag, replaceStateUrl, Route } from '@mews-distributor/common';
import { createAction } from '@mews/framework';
import { createHandler } from '@mews/std';
import { fetchPaymentConfiguration } from '../api/actions/paymentConfigurations';
import { fetchCurrentDateAndOccupancyProductPricesWithCheck } from '../api/actions/productPricing';
import {
    fetchReservationsCurrencyData,
    fetchReservationsPrice,
    fetchResourceOrderReservationPricingWithUnavailableOccupancy,
} from '../api/actions/reservationPrices';
import { QUERY_PARAM_HOTEL_ID, QUERY_PARAM_RESERVATION_GROUP_ID, QUERY_PARAM_RESERVATION_IDS } from '../constants';
import { VOUCHER_CODE_SUBMITTED } from '../middlewares/gtmActions';
import { ROUTE_SET, ROUTE_SET_PREVENTED } from '../modules/app/actionTypes';
import { getCurrencyCode, getCurrentRoute, getSelectedCityId } from '../modules/app/selectors';
import { shouldGetSelectedCityAvailability } from '../modules/availability/cityAvailability';
import { isMultiHotel, isStandalone } from '../modules/configuration/selectors';
import {
    areReservationsWithPromotedServices,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
} from '../modules/entities/reservations/selectors';
import { fetchCurrentDatesAvailabilityWithSelectedCurrencyCode } from '../modules/entities/services/actions';
import { shouldGetPaymentConfiguration } from '../modules/paymentConfigurations/selectors';
import { shouldGetSelectedNonEmptyOccupancyPricing } from '../modules/pricing/selectors';
import { fetchPromotedServices } from '../modules/promotedServices';
import { fetchCityResourceCategoriesWithCheck } from '../modules/resourceCategories/actions';
import { areDatesSelected, getStartTimeUtc, getVoucherCode } from '../modules/resourceOrder/selectors';

import { shouldGetSelectedCityPricing } from '../selectors/cityPricing';
import { hasSelectedEnterprisePciProxyStorage } from '../selectors/views/selectedEnterprisePaymentConfiguration';
import { isRecaptchaVerificationEnabled } from '../services/featureFlagService/featureFlags/recaptcha';
import { DistributorPromiseThunkAction, DistributorThunkAction } from '../types';
import { WithEnterpriseId, WithReservationGroupId } from '../types/selectors/selectorPropTypes';
import { getUrlPathWithParams } from '../utils/url';
import { fetchCheckoutDocuments } from '../views/checkout/data/actions';
import { hasReservationsPrices } from '../views/checkout/data/selectors';
import { hasCheckoutDocuments, shouldCheckoutPreparationStart } from '../views/checkout/data/viewSelectors';
import {
    CHECKOUT_PREPARATION_FAILED,
    CHECKOUT_PREPARATION_SKIPPED,
    CHECKOUT_PREPARATION_STARTED,
    CHECKOUT_PREPARATION_SUCCEEDED,
} from '../views/checkout/slices/checkout/actionTypes';
import { getValidStartTime } from '../views/dates/actions';
import { isTimeSelected, timesAreUserSelected } from '../views/dates/slices/calendar';
import { ratesPreparationFailed, ratesPreparationStarted, ratesPreparationSucceeded } from '../views/rates/reducer';
import {
    spaceCategoriesViewPreparationFailed,
    spaceCategoriesViewPreparationStarted,
    spaceCategoriesViewPreparationSucceeded,
} from '../views/spaceCategories/reducer';
import {
    summaryPreparationFailed,
    summaryPreparationStarted,
    summaryPreparationSucceeded,
} from '../views/summary/data/reducer';
import { clearTimes } from './dates';

export function setRoute(route: Route): DistributorThunkAction {
    return (dispatch, getState) => {
        const currentRoute = getCurrentRoute(getState());
        if (equals(currentRoute, route)) {
            return dispatch(createAction(ROUTE_SET_PREVENTED));
        }

        return dispatch(createAction(ROUTE_SET, { route }));
    };
}

// #region Dates

const createDateStepSubmittedTrackingEvents = (): DistributorThunkAction => (dispatch, getState) => {
    const state = getState();
    const voucherCode = getVoucherCode(state);
    if (!isEmpty(voucherCode)) {
        dispatch(createAction(VOUCHER_CODE_SUBMITTED, { voucherCode }));
    }
};

export function showDatesNextTab(): DistributorPromiseThunkAction {
    return async (dispatch, getState) => {
        const startTime = getStartTimeUtc(getState());
        // if user has selected only start time, we can consider it as user selected time
        if (startTime != null) {
            dispatch(timesAreUserSelected(true));
        }
        dispatch(createDateStepSubmittedTrackingEvents());
        if (isMultiHotel(getState())) {
            await dispatch(showProperties());
        } else {
            await dispatch(showSpaceCategories());
        }
    };
}

export const showDates = (): DistributorThunkAction => (dispatch, getState) => {
    const isTimeUserSelected = isTimeSelected(getState());
    if (!isTimeUserSelected) {
        dispatch(clearTimes());
    }
    return dispatch(setRoute(Route.Dates));
};

// #endregion

// #region Properties

export const showProperties = (): DistributorPromiseThunkAction => async (dispatch, getState) => {
    const state = getState();
    const dates = areDatesSelected(state);
    if (!dates) {
        return;
    }

    dispatch(setRoute(Route.Properties));

    const cityId = getSelectedCityId(state);
    if (cityId != null) {
        await dispatch(fetchCityResourceCategoriesWithCheck(cityId));
    }

    if (shouldGetSelectedCityAvailability(state, {}) || shouldGetSelectedCityPricing(state)) {
        void dispatch(fetchCurrentDatesAvailabilityWithSelectedCurrencyCode());
    }
};

// #endregion

// #region Categories

export function showSpaceCategoriesPreviousTab(): DistributorThunkAction {
    return (dispatch, getState) => {
        if (isMultiHotel(getState())) {
            void dispatch(showProperties());
        } else {
            dispatch(showDates());
        }
    };
}

export const fetchCategoriesViewDependencies = (): DistributorPromiseThunkAction => async (dispatch, getState) => {
    const state = getState();
    dispatch(spaceCategoriesViewPreparationStarted());

    try {
        const cityId = getSelectedCityId(state);
        if (cityId != null) {
            await dispatch(fetchCityResourceCategoriesWithCheck(cityId));
        }

        if (shouldGetSelectedCityAvailability(state, {}) || shouldGetSelectedCityPricing(state)) {
            await dispatch(fetchCurrentDatesAvailabilityWithSelectedCurrencyCode());
        }

        dispatch(spaceCategoriesViewPreparationSucceeded());
    } catch (error) {
        dispatch(spaceCategoriesViewPreparationFailed());

        throw error;
    }
};

export const showSpaceCategories = (): DistributorPromiseThunkAction => async (dispatch, getState) => {
    const dates = areDatesSelected(getState());

    if (!dates) {
        return;
    }
    dispatch(getValidStartTime());

    dispatch(setRoute(Route.SpaceCategories));
    await dispatch(fetchCategoriesViewDependencies());
};

// #endregion

// #region Rates

interface ShowRatesOptions {
    categoryId?: string;
    onResetEnterpriseSpaceTypeCount?: (categoryId: string) => void;
}

export function showRates({
    categoryId,
    onResetEnterpriseSpaceTypeCount,
}: ShowRatesOptions = {}): DistributorThunkAction {
    return (dispatch, getState, { loggerService }) => {
        const state = getState();
        const dates = areDatesSelected(state);
        if (!dates) {
            return;
        }
        dispatch(getValidStartTime());
        dispatch(ratesPreparationStarted());
        Promise.resolve()
            .then(async () => {
                const cityId = getSelectedCityId(state);
                if (cityId != null) {
                    return dispatch(fetchCityResourceCategoriesWithCheck(cityId));
                }
            })
            .then(async () => {
                if (shouldGetSelectedCityAvailability(state, {}) || shouldGetSelectedCityPricing(state)) {
                    return dispatch(fetchCurrentDatesAvailabilityWithSelectedCurrencyCode());
                }
            })
            .then(() => {
                if (onResetEnterpriseSpaceTypeCount != null && categoryId != null) {
                    onResetEnterpriseSpaceTypeCount(categoryId);
                }
            })
            .then(async () => {
                if (shouldGetSelectedNonEmptyOccupancyPricing(getState())) {
                    return dispatch(fetchResourceOrderReservationPricingWithUnavailableOccupancy());
                }
            })
            .then(async () => {
                const currencyCode = getCurrencyCode(getState());

                if (currencyCode == null) {
                    throw new Error('Currency code is undefined. Cannot fetch rates step data.');
                }

                return dispatch(fetchCurrentDateAndOccupancyProductPricesWithCheck({ currencyCode }));
            })
            .then(async () => {
                const currencyCode = getCurrencyCode(state);
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (currencyCode && !areReservationsWithPromotedServices(state)) {
                    return dispatch(fetchPromotedServices({ currencyCode }));
                }
            })
            .then(() => {
                dispatch(ratesPreparationSucceeded());
            })
            .catch((error) => {
                loggerService.log('prepare-rates-screen-error', 'Error when fetching data for rates screen', {
                    error,
                });
                dispatch(ratesPreparationFailed());
            });

        dispatch(setRoute(Route.Rates));
    };
}

// #endregion

// #region Summary

export const prepareSummary = (): DistributorPromiseThunkAction => async (dispatch, getState) => {
    dispatch(summaryPreparationStarted());
    try {
        const state = getState();
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (shouldGetPaymentConfiguration(state, {})) {
            await dispatch(fetchPaymentConfiguration());
        }
        await dispatch(
            fetchReservationsCurrencyData({
                currencyCode: getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(getState(), {}),
            })
        );
        await dispatch(fetchReservationsPrice());
        dispatch(summaryPreparationSucceeded());
    } catch (error) {
        dispatch(summaryPreparationFailed());
        throw error;
    }
};

export const showSummary = (): DistributorThunkAction => (dispatch) => {
    void dispatch(prepareSummary());
    dispatch(setRoute(Route.Summary));
};

// #endregion

// #region Checkout

export function showCheckout(): DistributorThunkAction {
    return (dispatch) => {
        void dispatch(prepareCheckout());
        dispatch(setRoute(Route.Checkout));
    };
}

const initRecaptcha =
    (): DistributorPromiseThunkAction =>
    async (_dispatch, getState, { environmentService, featureFlagService, recaptchaService, loggerService }) => {
        try {
            await recaptchaService.initialize({ shouldHideBadge: true });
        } catch (e) {
            const isRecaptchaEnabled = isRecaptchaVerificationEnabled(getState(), {
                environmentService,
                featureFlagService,
            });

            if (isRecaptchaEnabled) {
                throw e;
            } else {
                loggerService.log('recaptcha-init-failed', 'Recaptcha init failed', {
                    isRecaptchaEnabled: false,
                    error: e,
                });
            }
        }
    };

export function prepareCheckout(): DistributorThunkAction {
    return async (dispatch, getState, { pciProxyService, featureFlagService }) => {
        const originalState = getState();
        const isPciProxyMaintenanceDisabledPaymentsEnabled = featureFlagService.hasFlag(
            FeatureFlag.pciProxyMaintenanceDisabledPaymentsEnabled
        );
        if (shouldCheckoutPreparationStart(originalState)) {
            dispatch(createAction(CHECKOUT_PREPARATION_STARTED));

            try {
                const state = getState();

                const promises = [
                    hasReservationsPrices(state) ? Promise.resolve() : dispatch(fetchReservationsPrice()),
                    hasSelectedEnterprisePciProxyStorage(state, {}) &&
                        !isPciProxyMaintenanceDisabledPaymentsEnabled &&
                        pciProxyService.load(),
                    dispatch(initRecaptcha()),
                    hasCheckoutDocuments(state) ? Promise.resolve() : dispatch(fetchCheckoutDocuments()),
                ];

                await Promise.all(promises);

                dispatch(createAction(CHECKOUT_PREPARATION_SUCCEEDED));
            } catch (error) {
                dispatch(createAction(CHECKOUT_PREPARATION_FAILED));
                throw error;
            }
        } else {
            dispatch(createAction(CHECKOUT_PREPARATION_SKIPPED));
        }
    };
}

// #endregion

// #region Confirmation

export interface ConfirmationScreenMandatoryQueryData extends WithEnterpriseId, WithReservationGroupId {
    reservationIds: string[];
}

export const preserveConfirmationParams =
    ({
        enterpriseId,
        reservationGroupId,
        reservationIds,
    }: Partial<ConfirmationScreenMandatoryQueryData> = {}): DistributorThunkAction =>
    (dispatch, getState, { environmentService }) => {
        // sets query parameters for confirmation screen to be refreshable
        if (isStandalone(getState()) && enterpriseId != null && reservationGroupId != null) {
            const { href } = environmentService.get('location');
            const updatedUrl = getUrlPathWithParams(new URL(href), {
                [QUERY_PARAM_HOTEL_ID]: enterpriseId,
                [QUERY_PARAM_RESERVATION_GROUP_ID]: reservationGroupId,
                ...(reservationIds != null && reservationIds.length > 0
                    ? { [QUERY_PARAM_RESERVATION_IDS]: reservationIds.join(',') }
                    : {}),
            });
            dispatch(replaceStateUrl(updatedUrl));
        }
    };

export const showConfirmation = (): DistributorThunkAction => setRoute(Route.Confirmation);

// #endregion

export const tabClickHandler = createHandler({
    [Route.Dates]: showDates,
    [Route.Properties]: showProperties,
    [Route.SpaceCategories]: showSpaceCategories,
    [Route.Rates]: showRates,
    [Route.Summary]: showSummary,
    [Route.Checkout]: showCheckout,
});

export const navigateToUrl =
    (url: string): DistributorThunkAction =>
    (_dispatch, _getState, { environmentService }) => {
        environmentService.set('location.href', url);
    };
